// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, CircularProgress, Alert, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import { API_URL } from '../utils/api';

export default function PaymentPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === '1';

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (success) { setLoading(false); return; }
    fetch(`${API_URL}/public/pay/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setInfo)
      .catch(() => setError('Lien invalide ou expiré.'))
      .finally(() => setLoading(false));
  }, [token, success]);

  // Le motif de refus du serveur est affiché tel quel : un paiement fermé n'est
  // pas une panne, et « réessayez » ferait recommencer un geste qui ne peut pas
  // aboutir. Un message local ne sert que si le serveur n'en donne aucun.
  const handlePay = async () => {
    setRedirecting(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/public/pay/${token}/checkout`, { method: 'POST' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(body.detail
          || 'Une erreur est survenue. Écrivez-nous : equipe@vigeos.fr.');
        setRedirecting(false);
        return;
      }
      window.location.href = body.checkout_url;
    } catch {
      setError('Connexion impossible. Réessayez, ou écrivez-nous : equipe@vigeos.fr.');
      setRedirecting(false);
    }
  };

  // Faux seulement si le serveur le dit : sans ce champ, le bouton reste
  // affiché et un éventuel refus s'affiche au clic.
  const paymentOpen = info?.paiement_en_ligne !== false;

  // Page affichée sans la barre ni session, à un client arrivé par un lien reçu
  // par courriel. Aucun fond propre : le dégradé bleu nuit du thème est peint
  // sur le body.
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      {/* Ni rayon ni ombre ici : la carte de verre du thème porte les deux. */}
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        {/* Bandeau de marque : verre teinté souligné d'un filet, comme la
            console de contrats et la barre du dashboard. */}
        <Box sx={{ p: 4, bgcolor: 'action.selected', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight="bold">Vigeos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Surveillance à domicile</Typography>
        </Box>
        <CardContent sx={{ p: 4 }}>

          {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

          {/* Retour de Stripe après paiement */}
          {success && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6">Paiement reçu !</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Merci ! Vous allez recevoir un email pour choisir votre créneau d'installation.
              </Typography>
            </Box>
          )}

          {!success && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Déjà payé */}
          {!success && !loading && !error && info?.already_paid && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6">Paiement déjà effectué</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Merci {info.name} — consultez votre email pour planifier l'installation.
              </Typography>
            </Box>
          )}

          {/* Récapitulatif et paiement */}
          {!success && !loading && !error && info && !info.already_paid && (
            <>
              <Typography variant="h6" gutterBottom>Récapitulatif</Typography>
              {/* Récapitulatif creusé dans la carte pour se détacher. Rayon en
                  pixels : l'unité de `borderRadius` vaut 14 px dans le thème,
                  « 2 » donnerait 28 px. */}
              <Box sx={{ bgcolor: 'rgba(4,9,20,.45)', borderRadius: '16px', p: 3, my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography color="text.secondary">Client</Typography>
                  <Typography fontWeight="medium">{info.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography color="text.secondary">Formule</Typography>
                  {/* Pastille pleine : contournée, elle s'écrirait en #2563EB,
                      bleu fait pour porter du texte blanc et peu lisible sur le
                      bleu nuit. Pleine, elle prend la pastille du thème, fond
                      bleu à 22 % et texte ciel. */}
                  <Chip label={info.plan} size="small" color="primary" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography color="text.secondary">1ère mensualité</Typography>
                  {/* Montant en `secondary`, le bleu ciel des chiffres mis en
                      avant : `primary` se noierait dans le fond. */}
                  <Typography variant="h5" fontWeight="bold" color="secondary.main">
                    {Number(info.price).toFixed(2)} €
                  </Typography>
                </Box>
              </Box>

              {/* Quand le paiement en ligne est fermé, le récapitulatif reste
                  affiché pour que le client retrouve de quoi il s'agit ; seul
                  le bouton cède la place à l'explication. */}
              {paymentOpen ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={redirecting ? null : <CreditCardIcon />}
                    onClick={handlePay}
                    disabled={redirecting}
                    sx={{ py: 1.5, mb: 2 }}
                  >
                    {redirecting
                      ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Redirection…</>
                      : 'Payer par carte'}
                  </Button>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.disabled">
                      Paiement sécurisé par Stripe — vos données bancaires ne nous sont jamais transmises
                    </Typography>
                  </Box>
                </>
              ) : (
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 1 }}>
                  {info.motif}
                </Alert>
              )}
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
            {/* Pas de couleur propre : CssBaseline pose le bleu ciel du thème
                sur les <a> bruts. */}
            Questions ? <a href="mailto:equipe@vigeos.fr">equipe@vigeos.fr</a>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
